import React from 'react'
import { Tooltip, Button } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { FilterFilled } from '@ant-design/icons'
import { sortBy } from 'lodash'
import {
  faHome,
  faCheckCircle,
  faClinicMedical,
  faTimesCircle,
  faVideo,
  faHeartPulse,
  faPersonWalking
} from '@fortawesome/free-solid-svg-icons'
import theme from '../../../../core/utils/theme'

/**
 * Generates the column configuration for the clinics list table
 *
 * @param {Function} editRecord - Function to handle editing a clinic record
 * @param {Object} filters - Filter options for the table
 * @returns {Array} Column configuration array
 */
export const getClinicColumns = (editRecord, filters) => {
  return [
    {
      title: '',
      key: 'action',
      align: 'center',
      fixed: 'left',
      render: (_, record) => (
        <Tooltip title='Edit facility details'>
          <Button
            type='primary'
            size='small'
            onClick={(e) => {
              e.stopPropagation() // Prevent row click event
              editRecord(record)
            }}
            style={{
              borderRadius: '6px',
              fontWeight: '500',
              fontSize: '12px'
            }}
          >
            Edit
          </Button>
        </Tooltip>
      )
    },
    {
      title: 'Facility Name',
      dataIndex: 'name',
      width: '20%',
      key: 'name',
      sorter: (a, b) => {
        return a.name.localeCompare(b.name)
      },
      render: (text) => <span style={{ fontWeight: 600, color: '#1f1f1f' }}>{text}</span>
    },
    {
      title: 'Facility Type',
      dataIndex: 'clinicType',
      key: 'clinicType',
      width: '15%',
      filters: filters.clinicType,
      onFilter: (value, record) => {
        return record.clinicType.indexOf(value) === 0
      },
      filterIcon: (filtered) => (
        <Tooltip title='Filter by Facility Type'>
          <FilterFilled style={{ color: filtered ? theme.twitterBootstrap.primary : undefined }} />
        </Tooltip>
      ),
      sorter: (a, b) => {
        return a.clinicType.localeCompare(b.clinicType)
      },
      render: (text) => (
        <span
          style={{
            padding: '3px 10px',
            borderRadius: '12px',
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
            fontSize: '0.85rem',
            display: 'inline-block',
            lineHeight: '1.5',
            fontWeight: 500
          }}
        >
          {text}
        </span>
      )
    },
    {
      width: '12%',
      title: 'Province',
      dataIndex: 'province',
      key: 'province',
      filterSearch: true,
      filters: sortBy(filters.province, ['text']),
      onFilter: (value, record) => {
        return record.province.indexOf(value) === 0
      },
      filterIcon: (filtered) => (
        <Tooltip title='Filter by Province'>
          <FilterFilled style={{ color: filtered ? theme.twitterBootstrap.primary : undefined }} />
        </Tooltip>
      ),
      sorter: (a, b) => {
        return a.province.localeCompare(b.province)
      },
      render: (text) => {
        // Generate a consistent color based on the province name
        const getProvinceColor = (province) => {
          const colors = {
            Gauteng: '#1890ff',
            'Eastern Cape': '#722ed1',
            'Western Cape': '#13c2c2',
            'Free State': '#fa8c16',
            'North West': '#eb2f96',
            Limpopo: '#52c41a',
            Mpumalanga: '#faad14',
            'Northern Cape': '#fa541c',
            'KwaZulu-Natal': '#2f54eb'
          }
          return colors[province] || '#1890ff'
        }

        return (
          <span
            style={{
              padding: '3px 10px',
              borderRadius: '12px',
              backgroundColor: `${getProvinceColor(text)}15`,
              color: getProvinceColor(text),
              fontSize: '0.85rem',
              display: 'inline-block',
              lineHeight: '1.5',
              fontWeight: 500
            }}
          >
            {text}
          </span>
        )
      }
    },
    {
      title: 'Physical Address',
      dataIndex: 'physicalAddress',
      key: 'address',
      width: '33%',
      ellipsis: {
        showTitle: false
      },
      render: (text) => (
        <Tooltip title={text} placement='topLeft'>
          <span style={{ color: '#595959' }}>{text}</span>
        </Tooltip>
      )
    },
    {
      title: 'Status',
      width: '10%',
      align: 'center',
      dataIndex: 'isActive',
      key: 'status',
      filters: [
        { text: 'Active', value: true },
        { text: 'Inactive', value: false }
      ],
      onFilter: (value, record) => record.isActive === value,
      filterIcon: (filtered) => (
        <Tooltip title='Filter by Status'>
          <FilterFilled style={{ color: filtered ? theme.twitterBootstrap.primary : undefined }} />
        </Tooltip>
      ),
      sorter: (a, b) => {
        return a.isActive === b.isActive ? 0 : a.isActive ? -1 : 1
      },
      render: (isActive) => {
        return (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            {isActive ? (
              <Tooltip title='Active'>
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    backgroundColor: `${theme.twitterBootstrap.success}15`,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                  }}
                >
                  <FontAwesomeIcon
                    icon={faCheckCircle}
                    style={{ fontSize: 16, color: theme.twitterBootstrap.success }}
                  />
                </span>
              </Tooltip>
            ) : (
              <Tooltip title='Inactive'>
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    backgroundColor: `${theme.twitterBootstrap.danger}15`,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                  }}
                >
                  <FontAwesomeIcon
                    icon={faTimesCircle}
                    style={{ fontSize: 16, color: theme.twitterBootstrap.danger }}
                  />
                </span>
              </Tooltip>
            )}
          </div>
        )
      }
    },
    {
      title: 'Services',
      width: '18%',
      align: 'center',
      render: (record) => {
        return (
          <div
            style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' }}
          >
            {/* Facility Appointments - Always shown in first position */}
            <Tooltip title={record.clinicBookings ? 'Appointments Available' : 'Appointments Not Available'}>
              <FontAwesomeIcon
                icon={faClinicMedical}
                className='service-icon'
                style={{
                  fontSize: 16,
                  color: record.clinicBookings ? theme.twitterBootstrap.primary : '#ccc',
                  opacity: record.clinicBookings ? 1 : 0.5,
                  transition: 'all 0.2s ease'
                }}
              />
            </Tooltip>

            {/* Home Visits - Always shown in second position */}
            <Tooltip title={record.homeVisits ? 'Home Visits Available' : 'Home Visits Not Available'}>
              <FontAwesomeIcon
                icon={faHome}
                className='service-icon'
                style={{
                  fontSize: 16,
                  color: record.homeVisits ? theme.twitterBootstrap.info : '#ccc',
                  opacity: record.homeVisits ? 1 : 0.5,
                  transition: 'all 0.2s ease'
                }}
              />
            </Tooltip>

            {/* Telehealth Services - Always shown in third position */}
            <Tooltip title={record.telehealth ? 'Telehealth Services Available' : 'Telehealth Services Not Available'}>
              <FontAwesomeIcon
                icon={faVideo}
                className='service-icon'
                style={{
                  fontSize: 16,
                  color: record.telehealth ? theme.twitterBootstrap.success : '#ccc',
                  opacity: record.telehealth ? 1 : 0.5,
                  transition: 'all 0.2s ease'
                }}
              />
            </Tooltip>

            {/* Vitals Monitoring - Always shown in fourth position */}
            <Tooltip
              title={record.vitalsMonitoring ? 'Vitals Monitoring Available' : 'Vitals Monitoring Not Available'}
            >
              <FontAwesomeIcon
                icon={faHeartPulse}
                className='service-icon'
                style={{
                  fontSize: 16,
                  color: record.vitalsMonitoring ? '#20b2aa' : '#ccc',
                  opacity: record.vitalsMonitoring ? 1 : 0.5,
                  transition: 'all 0.2s ease'
                }}
              />
            </Tooltip>

            {/* Walk-ins - Always shown in fifth position */}
            <Tooltip title={record.walkIns ? 'Walk-ins Available' : 'Walk-ins Not Available'}>
              <FontAwesomeIcon
                icon={faPersonWalking}
                className='service-icon'
                style={{
                  fontSize: 16,
                  color: record.walkIns ? '#8e44ad' : '#ccc',
                  opacity: record.walkIns ? 1 : 0.5,
                  transition: 'all 0.2s ease'
                }}
              />
            </Tooltip>
          </div>
        )
      }
    }
  ]
}
